import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import { disablePush, enablePush, getPushStatus, listenForegroundPushes, syncPush, type PushStatus } from './push';

/** Push opt-in state for the logged-in account in this browser. `status` is null while checking. */
export function usePush() {
  const { user } = useAuth();
  const userId = user?.id;
  const [status, setStatus] = useState<PushStatus | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    let alive = true;
    getPushStatus(userId)
      .then((next) => alive && setStatus(next))
      .catch(() => alive && setStatus('unsupported'));
    return () => {
      alive = false;
    };
  }, [userId]);

  async function enable() {
    if (!userId) return;
    setIsBusy(true);
    setError(null);
    try {
      setStatus(await enablePush(userId));
    } catch {
      setError('Não foi possível ativar as notificações agora. Tente novamente em instantes.');
    } finally {
      setIsBusy(false);
    }
  }

  async function disable() {
    setIsBusy(true);
    setError(null);
    try {
      await disablePush();
      setStatus('default');
    } finally {
      setIsBusy(false);
    }
  }

  return { status, isBusy, error, enable, disable };
}

let syncedForUser: number | null = null;

/**
 * Mounted once in the app shell: refreshes this browser's token once per session and, while the
 * dashboard is open, turns incoming pushes into a refresh of the notifications/feed lists.
 */
export function usePushSync() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;

  useEffect(() => {
    if (!userId) return;
    let unsubscribe: (() => void) | undefined;
    let alive = true;

    (async () => {
      if ((await getPushStatus(userId)) !== 'enabled' || !alive) return;
      if (syncedForUser !== userId) {
        syncedForUser = userId;
        await syncPush(userId).catch(() => undefined);
      }
      const stop = await listenForegroundPushes(() => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['tenders'] });
      });
      if (alive) unsubscribe = stop;
      else stop();
    })().catch(() => undefined);

    return () => {
      alive = false;
      unsubscribe?.();
    };
  }, [userId, queryClient]);
}
